/**
 * `heysegment codegen` — generate the SDK databases registry.
 *
 * getSessionSiteDatabases (RPC) → generate `src/lib/databases.ts` from the session
 * workspace's database definitions → patch `src/lib/sdk.ts` to import it →
 * report `codegen.generated` (best-effort).
 *
 * The workspace is never named on the wire: the server derives it from the
 * authenticated session, so the registry always matches the session's workspace.
 *
 * @module
 */
import { Console, Effect, FileSystem } from "effect"
import { Command } from "effect/unstable/cli"
import { applyDatabaseCodegen, DATABASES_PATH } from "@repo/services/site-database-codegen"
import { withSession, step } from "../session/with-session"
import { SessionClient } from "../rpc"
import { apiUrlFlag, dirFlag } from "./flags"

const plural = (n: number): string => (n === 1 ? "" : "s")

export const codegenCommand = Command.make(
  "codegen",
  { dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, (session) =>
      Effect.gen(function* () {
        const fs = yield* FileSystem.FileSystem
        const client = yield* SessionClient

        const { databases } = yield* step(
          "codegen:fetch",
          client.getSessionSiteDatabases(void 0),
        )

        const result = yield* step(
          "codegen:write",
          applyDatabaseCodegen({
            fs,
            projectDir: session.projectDir,
            databases,
          }),
        )

        // Best-effort telemetry — never fail the command on a reporting hiccup.
        yield* client
          .report({ type: "codegen.generated", data: { count: result.count } })
          .pipe(Effect.ignore)

        yield* Console.log(
          result.wrote
            ? `✓ Generated ${DATABASES_PATH} (${result.count} database${plural(result.count)})`
            : `✓ Databases registry already up to date (${result.count} database${plural(result.count)})`,
        )
      }),
    ),
).pipe(
  Command.withDescription(
    "Generate the SDK databases registry from the workspace's onboarded databases",
  ),
)
